import { useEffect, useRef, useState } from 'react'
import { DefaultTheme } from 'styled-components'
import Box from 'components/shared/Box'
import Text from 'components/shared/Text'
import Price from 'components/shared/Price'
import Button from 'components/shared/Button'
import {
    Wrapper,
    HeaderWrapper,
    PriceWrapper,
    ZonesWrapper,
    Image,
    Flag,
    ZonesWrapperProps,
} from './styles'
import LogoJamango from 'assets/images/logos/Jamango.svg'
import LogoRelax from 'assets/images/logos/Relax.svg'
import LogoSaunaria from 'assets/images/logos/Saunaria.svg'
import LogoJamangoDarkMode from 'assets/images/logos/Jamango-mode_dark.svg'
import LogoRelaxDarkMode from 'assets/images/logos/Relax-mode_dark.svg'
import LogoSaunariaDarkMode from 'assets/images/logos/Saunaria-mode_dark.svg'
import { FormattedMessage } from 'react-intl'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import {
    setCardType,
    setIsCalendarModalOpened,
    setIsUnlimitedPlusBeenOffered,
} from 'store/unlimited-slice'
import { useAutoScroll } from 'utils/hooks'
import {
    CardDetailDtoLimited,
    CardDtoWithBundle,
    CardEditionType,
} from 'store/api-slice/types'
import UpgradeCardModal from 'routes/unlimited-card/CardConfiguration/UnlimitedCardConfigurator/UpgradeCardModal'
import { useUnlimited } from 'store/unlimited-slice/hooks'
import { useCart } from 'store/cart-slice/hooks'
import {
    calculateUnlimitedAddToCartEvent,
    pushDataLayer,
} from 'helpers/analytics'
import { usePromotions } from 'components/shared/Promotions/utils/usePromotions'
import RenewTitledAdvantages from 'routes/unlimited-card/RenewSummary/components/RenewTitledAdvantages'
import Features from './components/Features'

interface UnlimitedCardProps {
    index: number
    cardSpecifications: CardDtoWithBundle
    unlimitedPlusData: CardDtoWithBundle
    theme?: DefaultTheme
    isRenewed?: boolean
    isSingleRenewed?: boolean
    arePairedCtaLocked?: boolean
    setArePairedCtaLocked?: (areCtaLocked: boolean) => void
    zonesPosition?: 'top' | 'bottom'
    areZonesCentered?: boolean
    cardEdition?: CardEditionType
}

// TODO: a big refactor needed
// - remove all 'unlimited only' redux logic to wrapping components
// - make it universal for vouchers/unlimited cards/renewed cards
// - divide it into smaller pieces

const UnlimitedCard = ({
    index,
    cardSpecifications,
    unlimitedPlusData,
    theme,
    isRenewed,
    isSingleRenewed,
    arePairedCtaLocked,
    setArePairedCtaLocked,
    zonesPosition = 'bottom',
    areZonesCentered = false,
    cardEdition,
}: UnlimitedCardProps) => {
    const timeoutRef = useRef<NodeJS.Timeout>()
    const [isModalOpen, setIsModalOpen] = useState(false)
    const analyticsRef = useRef(false)
    const dispatch = useAppDispatch()
    const { calculateScroll } = useAutoScroll()
    const { isDarkThemeForUnlimited } = usePromotions()
    const { isCartEmpty, isCartFetching, isSummaryFetching } = useCart()
    const {
        addCardsToCart,
        clearItemsFromCart,
        isProcessing: areCardsProcessing,
    } = useUnlimited()

    const { items, cart } = useAppSelector((state) => state.cart)
    const { cards } = items
    const { cardBoxes } = useAppSelector((state) => state.unlimited)
    const { isUnlimitedPlusBeenOffered } = useAppSelector(
        (state) => state.unlimited
    )

    const [isCurrentCardProcessing, setIsCurrentCardProcessing] =
        useState(false)

    const { detail, offers, product, zones, bundleData } = cardSpecifications
    const { productId, productType, unitPrice } = product || {}

    const shouldCtaLoading =
        isSummaryFetching || areCardsProcessing || arePairedCtaLocked
    const isCurrentCardChosen = cardBoxes[index]?.productId === productId
    const isCurrentCardUnlimitedPlus =
        detail?.cardType?.value?.includes('unlimited_plus') || false
    const isScrollOff =
        document.documentElement.classList.contains('-modal-open')

    const elementCalculateScroll = () =>
        calculateScroll({
            trigerringSection: 'card_configuration',
            trigerringAction: 'show',
            targetId: `cardOwnerData${index}`,
        })

    const handleChooseCardType = () => {
        if (isRenewed) {
            timeoutRef.current && clearTimeout(timeoutRef.current)
            setArePairedCtaLocked && setArePairedCtaLocked(true)
            setIsCurrentCardProcessing(true)
        }

        if (
            !isUnlimitedPlusBeenOffered &&
            !isCurrentCardUnlimitedPlus &&
            !isRenewed
        ) {
            dispatch(setIsUnlimitedPlusBeenOffered(true))
            !isScrollOff &&
                document.documentElement.classList.add('-modal-open')
            return setIsModalOpen(true)
        }

        productId &&
            productType &&
            detail?.cardType &&
            dispatch(
                setCardType({
                    index,
                    productId,
                    // TODO: ask backend to unify types
                    cardDetail: detail as CardDetailDtoLimited,
                    bundleData,
                })
            )
        elementCalculateScroll()
    }

    const handleCancelCardTypeChoice = () => {
        timeoutRef.current && clearTimeout(timeoutRef.current)
        setArePairedCtaLocked && setArePairedCtaLocked(true)
        setIsCurrentCardProcessing(true)
        dispatch(setCardType({ index }))
    }

    const handleUpgradeCard = () => {
        const { product, detail, bundleData } = unlimitedPlusData
        const { productId, productType } = product || {}

        productId &&
            productType &&
            detail?.cardType &&
            dispatch(
                setCardType({
                    index,
                    productId,
                    // TODO: ask backend to unify types
                    cardDetail: detail as CardDetailDtoLimited,
                    bundleData,
                })
            )
        document.documentElement.classList.remove('-modal-open')
        setIsModalOpen(false)
        elementCalculateScroll()
    }

    const handleChooseCurrentCardType = () => {
        productId &&
            productType &&
            detail?.cardType &&
            dispatch(
                setCardType({
                    index,
                    productId,
                    // TODO: ask backend to unify types
                    cardDetail: detail as CardDetailDtoLimited,
                    bundleData,
                })
            )
        document.documentElement.classList.remove('-modal-open')
        setIsModalOpen(false)
        elementCalculateScroll()
    }

    const setZoneLogo = (zone: string) => {
        interface Logotypes {
            [key: string]: {
                [key: string]: string
            }
        }

        const logotypes: Logotypes = {
            jamango: {
                light: LogoJamango,
                dark: LogoJamangoDarkMode,
            },
            relax: {
                light: LogoRelax,
                dark: LogoRelaxDarkMode,
            },
            saunaria: {
                light: LogoSaunaria,
                dark: LogoSaunariaDarkMode,
            },
        }
        const mode = isDarkThemeForUnlimited ? 'dark' : 'light'

        return logotypes[zone][mode]
    }

    const afterFinishTransactions = () => {
        timeoutRef.current && clearTimeout(timeoutRef.current)

        timeoutRef.current = setTimeout(() => {
            setIsCurrentCardProcessing(false)
        }, 500)
    }

    const openCalendarModal = () => {
        dispatch(setIsCalendarModalOpened(true))
    }

    useEffect(() => {
        // < analytics
        if (!isRenewed || isCartFetching || !cards.length) return

        if (!analyticsRef.current) {
            const unlimitedAddToCartEvent = calculateUnlimitedAddToCartEvent(
                cards,
                cart
            )
            pushDataLayer(unlimitedAddToCartEvent)
            analyticsRef.current = true
        }
        // analytics >
    }, [isCartFetching, cards.length])

    useEffect(() => {
        // unlock paired cards after end of processing cart transaction
        // and unlocking current card
        if (isCurrentCardProcessing || !setArePairedCtaLocked) return
        setArePairedCtaLocked && setArePairedCtaLocked(false)
    }, [isCurrentCardProcessing])

    useEffect(() => {
        if (!isRenewed) return

        // < analytics
        if (isCurrentCardChosen)
            pushDataLayer({
                event: 'funnelStep',
                event_category: 'unlimited_renew',
                event_action: 'unlimited_params_selected',
                event_label: '',
            })
        // analytics >

        const cartProductId = items?.cards.length
            ? items.cards[0].productId
            : ''
        const isCurrentCardAlreadyInCart = cartProductId === productId

        if (isCurrentCardChosen && isCartEmpty) {
            addCardsToCart(true).then(() => afterFinishTransactions())
        }
        if (
            isCurrentCardChosen &&
            !isCartEmpty &&
            !isCurrentCardAlreadyInCart
        ) {
            clearItemsFromCart().then(() => {
                addCardsToCart(true).then(() => afterFinishTransactions())
            })
        }
        if (
            !isCurrentCardChosen &&
            !isCartEmpty &&
            isCurrentCardAlreadyInCart
        ) {
            clearItemsFromCart().then(() => afterFinishTransactions())
        }
    }, [isCurrentCardChosen])

    useEffect(() => {
        if (!isSingleRenewed) return

        dispatch(
            setCardType({
                index,
                productId,
                cardDetail: detail as CardDetailDtoLimited,
                bundleData,
            })
        )
    }, [])

    useEffect(
        () => () => {
            timeoutRef.current && clearTimeout(timeoutRef.current)
        },
        []
    )

    const renderZonesWrapper = (
        args: ZonesWrapperProps & { isCentered?: boolean } = {
            isCentered: true,
        }
    ) => (
        <ZonesWrapper
            pt={24}
            pb={zonesPosition === 'bottom' ? 0 : 24}
            isFullWidth
            {...args}
        >
            <Text
                variant="body"
                mb={8}
                color={
                    isDarkThemeForUnlimited ? 'grayscale_200' : 'grayscale_900'
                }
            >
                <FormattedMessage
                    id="annual.card_zones_label"
                    defaultMessage="Dostęp do stref:"
                />
            </Text>
            <Box direction="row" gap={16}>
                {zones?.map(
                    ({ name, value }, index) =>
                        value && (
                            <Image
                                key={`${name}${index}`}
                                src={setZoneLogo(value)}
                                alt=""
                            />
                        )
                )}
            </Box>
        </ZonesWrapper>
    )

    return (
        <Wrapper
            isChosen={isCurrentCardChosen}
            align="stretch"
            pt={24}
            pb={[24, 32]}
            pl={[24, 32]}
            pr={[24, 32]}
            theme={theme}
            isSingleRenewed={isSingleRenewed}
        >
            <HeaderWrapper
                isBorderBottom={false}
                isRenewed={isRenewed}
                isPlus={isCurrentCardUnlimitedPlus}
                isSingleRenewed={isSingleRenewed}
            >
                {!isRenewed && detail?.isRecommended && (
                    <Flag align="center">
                        <Text variant="bodyExtraSmall" color="grayscale_0">
                            <FormattedMessage
                                id="annual.card_recommended"
                                defaultMessage="Polecane"
                            />
                        </Text>
                    </Flag>
                )}
                <Text
                    variant="heading3"
                    color={
                        isDarkThemeForUnlimited ? 'grayscale_200' : undefined
                    }
                >
                    {detail?.name}
                </Text>
                {!isRenewed && (
                    <Text
                        variant="body"
                        color={
                            isDarkThemeForUnlimited
                                ? 'grayscale_500'
                                : 'grayscale_700'
                        }
                    >
                        {detail?.description}
                    </Text>
                )}
                <PriceWrapper direction="row" isSingleRenewed={isSingleRenewed}>
                    {!!unitPrice && (
                        <Price
                            value={unitPrice}
                            size="heading1"
                            color={
                                isDarkThemeForUnlimited
                                    ? 'primary_500'
                                    : 'grayscale_900'
                            }
                        />
                    )}
                    <Text
                        variant="bodyLarge"
                        color={
                            isDarkThemeForUnlimited
                                ? 'grayscale_200'
                                : 'grayscale_700'
                        }
                        ml={6}
                    >
                        <FormattedMessage
                            id="annual.per_year_label"
                            defaultMessage="za rok"
                        />
                    </Text>
                </PriceWrapper>
                {isCurrentCardChosen && !isSingleRenewed && (
                    <Button
                        variant="borderPrimary"
                        hasArrow={false}
                        width="100%"
                        onClick={handleCancelCardTypeChoice}
                        isLoading={isCurrentCardProcessing && shouldCtaLoading}
                        isDisabled={
                            (!isCurrentCardProcessing && arePairedCtaLocked) ||
                            arePairedCtaLocked
                        }
                        testId={`${
                            isRenewed ? 'renew' : `unlimited-${index}`
                        }-button-decline-card${
                            isCurrentCardUnlimitedPlus ? '-plus' : ''
                        }`}
                    >
                        <FormattedMessage
                            id="annual.btn.cancel_card"
                            defaultMessage="Anuluj wybór"
                        />
                    </Button>
                )}
                {!isCurrentCardChosen && !isSingleRenewed && (
                    <Button
                        variant="primary"
                        hasArrow={false}
                        width="100%"
                        onClick={handleChooseCardType}
                        isLoading={isCurrentCardProcessing && shouldCtaLoading}
                        isDisabled={
                            (!isCurrentCardProcessing && arePairedCtaLocked) ||
                            arePairedCtaLocked
                        }
                        testId={`${
                            isRenewed ? 'renew' : `unlimited-${index}`
                        }-button-choose-card${
                            isCurrentCardUnlimitedPlus ? '-plus' : ''
                        }`}
                    >
                        {isRenewed && !isCurrentCardUnlimitedPlus && (
                            <FormattedMessage
                                id="annual.renew.btn_renew_card"
                                defaultMessage="Odnów kartę Unlimited"
                            />
                        )}
                        {isRenewed && isCurrentCardUnlimitedPlus && (
                            <FormattedMessage
                                id="annual.renew.btn_upgrade_card"
                                defaultMessage="Zmień na kartę Unlimited+"
                            />
                        )}
                        {!isRenewed && (
                            <FormattedMessage
                                id="annual.btn.select_card"
                                defaultMessage="Wybierz kartę Unlimited"
                            />
                        )}
                        {!isRenewed && isCurrentCardUnlimitedPlus ? '+' : ''}
                    </Button>
                )}
            </HeaderWrapper>

            {zonesPosition === 'top' &&
                renderZonesWrapper({
                    isSingleRenewed,
                    isBorderTop: false,
                    isBorderBottom: true,
                    isCentered: areZonesCentered,
                })}

            <Features
                offers={offers}
                cardIndex={index}
                isCurrentCardUnlimitedPlus={isCurrentCardUnlimitedPlus}
                isRenewed={isRenewed}
                theme={theme}
                pt={24}
                pb={24}
            />
            {isRenewed && isSingleRenewed && (
                <RenewTitledAdvantages
                    mb={[0, 8]}
                    gap={24}
                    cardEdition={cardEdition}
                />
            )}
            {zonesPosition === 'bottom' &&
                renderZonesWrapper({
                    isSingleRenewed,
                })}
            <UpgradeCardModal
                isOpen={isModalOpen}
                onLinkClick={handleChooseCurrentCardType}
                onButtonClick={handleUpgradeCard}
                calendarModalToggle={openCalendarModal}
            />
        </Wrapper>
    )
}

export default UnlimitedCard
